import { AuthActions } from "../redux/Auth/actions";

const fetchWrapper =
    (url, options = {}, retry = 0) =>
    async (dispatch, getState) => {
        const isGetRequest = !options.method || options.method === "GET";
        const buildUrl = () =>
            isGetRequest
                ? `${url}${url.includes("?") ? "&" : "?"}_cachebuster=${Date.now()}`
                : url;

        options = {
            ...options,
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                ...options.headers,
            },
        };

        // A single 401 is not proof the session is gone: the ingress restarting
        // during a deploy briefly rejects requests, and a busy page would then
        // log the user out over a transient blip. Re-check a few times with a
        // short delay before concluding the session really expired -- a genuine
        // expiry keeps returning 401, a deploy blip clears. A 401 means the
        // request was rejected (not executed), so re-issuing it is safe.
        const UNAUTH_MAX_RETRIES = 4;
        const UNAUTH_RETRY_DELAY_MS = 2000;

        try {
            let response = await fetch(buildUrl(), options);
            for (
                let attempt = 0;
                response.status === 401 && attempt < UNAUTH_MAX_RETRIES;
                attempt++
            ) {
                await new Promise((resolve) =>
                    setTimeout(resolve, UNAUTH_RETRY_DELAY_MS)
                );
                response = await fetch(buildUrl(), options);
            }
            if (response.status === 401) {
                // Still unauthorized after retries: the session really expired.
                // A busy page has many requests in flight and they ALL come back
                // 401 at once - only the first one still in the logged-in state
                // performs the logout, the rest just fail.
                if (getState().Auth.isLoggedIn) {
                    dispatch(AuthActions.Logout());
                }
                throw new Error("Unauthorized Error");
            }

            if (!response.ok) {
                // The gateway refuses every UI request from a user who has not
                // accepted the current terms, and says so. Raise the consent
                // modal rather than showing an error the user cannot act on:
                // this is the path a session already open takes when new terms
                // are published.
                const refusal = await readErrorCode(response);
                if (refusal === "TERMS_NOT_ACCEPTED") {
                    dispatch(AuthActions.TermsRequired());
                    throw new Error("Terms not accepted");
                }
                throw new Error(
                    `Request failed with status: ${response.status}`
                );
            }

            let body;
            try {
                body = await response.json();
            } catch (parseError) {
                return {};
            }

            if (body.error) {
                throw new Error(
                    typeof body.error === "string"
                        ? body.error
                        : JSON.stringify(body.error)
                );
            }
            if (response.status === 500 && body.message) {
                throw new Error(`Server error: ${body.message}`);
            }
            return body;
        } catch (error) {
            console.error(`Fetch error for ${url}:`, error);
            if (retry > 0) {
                return dispatch(fetchWrapper(url, options, retry - 1));
            }
            throw new Error(`UI API request failed: ${error.message}`);
        }
    };

/**
 * The `error_code` on a refusal, or null when the body carries none. Reading it
 * consumes the body, which is why it is only reached for a response that has
 * already failed and whose body nothing else will read.
 */
const readErrorCode = async (response) => {
    try {
        const body = await response.clone().json();
        return body?.error_code || null;
    } catch (e) {
        return null;
    }
};

export default fetchWrapper;
